import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, ListItem, ListItemButton, Paper } from '@mui/material';
import {
  Language as LanguageIcon,
  AccountBalance as GovernmentIcon,
  Article as NewsIcon,
  Security as PrivateIcon,
} from '@mui/icons-material';
import {
  SourceItem,
  getSourceTypeLabel,
  getFaviconUrl,
  getPublisherName,
  groupSources,
  sourceGroupType,
  sourceSubtext,
} from './SourceTypes';
import { primitiveColors, semanticColors, BORDER_RADIUS } from '@/theme/tokens';
import useAnalytics from '@/hooks/useAnalytics';

interface SourceListProps {
  sources: SourceItem[];
  /**
   * Whether the body cites these sources, and so whether a row's group
   * position is a number the reader can match to a chip. False on a turn
   * answered without citations, where the rows are provenance rather than
   * references — see `bodyCitesAnySource`. Defaults to true so a caller that
   * has not been taught the distinction keeps the numbered presentation.
   */
  numbered?: boolean;
  /**
   * How many of `sources` a chip in the body points at. Cited sources lead the
   * array, so rows at or past this index are the rest of what the turn read.
   * Reported to analytics; it does not change how a row is numbered, because a
   * numbered group with holes in it reads as a rendering fault rather than as a
   * distinction. Defaults to every source, for a caller that passes a
   * cited-only list.
   */
  citedCount?: number;
}

function hostFromUrl(url?: string): string {
  if (!url) return '';
  try {
    let h = new URL(url).hostname;
    if (h.startsWith('www.')) h = h.slice(4);
    return h;
  } catch {
    return '';
  }
}

const SourceList: React.FC<SourceListProps> = ({ sources, numbered = true, citedCount }) => {
  const { t } = useTranslation('chat');
  const analytics = useAnalytics();

  if (!sources || sources.length === 0) {
    return null;
  }

  const getSourceIcon = (type: SourceItem['type'] | undefined, isAvailable: boolean) => {
    if (!isAvailable) {
      return <PrivateIcon sx={{ fontSize: 20, color: primitiveColors.rockSlate }} />;
    }

    switch (type) {
      case 'government':
      case 'official_state':
        return <GovernmentIcon sx={{ fontSize: 20, color: semanticColors.sourceGovernment }} />;
      case 'news':
        return <NewsIcon sx={{ fontSize: 20, color: semanticColors.sourceNews }} />;
      case 'votemate_curated':
        return <PrivateIcon sx={{ fontSize: 20, color: semanticColors.sourceCurated }} />;
      default:
        return <LanguageIcon sx={{ fontSize: 20, color: primitiveColors.rockSlate }} />;
    }
  };

  // Show the site's own favicon (e.g. Ballotpedia's icon) via Google's public
  // favicon service; fall back to the generic type glyph if it fails to load
  // or there's no usable URL.
  const SourceFavicon: React.FC<{
    url?: string;
    type?: SourceItem['type'];
    isAvailable: boolean;
  }> = ({ url, type, isAvailable }) => {
    const [failed, setFailed] = React.useState(false);
    const faviconUrl = isAvailable ? getFaviconUrl(url) : undefined;

    if (!faviconUrl || failed) {
      return getSourceIcon(type, isAvailable);
    }

    return (
      <Box
        component="img"
        src={faviconUrl}
        alt=""
        aria-hidden="true"
        onError={() => setFailed(true)}
        sx={{
          width: 20,
          height: 20,
          borderRadius: '4px',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  };

  // Grouping and the within-group numbering both live in SourceTypes so the
  // inline chips in the message body read the same numbers as these rows.
  const { groups } = groupSources(sources);
  const citedThrough = citedCount ?? sources.length;

  return (
    <Paper
      elevation={0}
      sx={{
        background: primitiveColors.white,
        borderRadius: BORDER_RADIUS,
        p: 1.5,
        border: `1px solid ${primitiveColors.rockSlate}20`,
      }}
    >
      {groups.map((group, groupIndex) => (
        <Box
          key={`${group.type}-${groupIndex}`}
          sx={{ mb: groupIndex < groups.length - 1 ? 1.5 : 0 }}
        >
          <Typography
            component="h4"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: semanticColors.textCaption,
              px: 0.5,
              mb: 0.25,
            }}
          >
            {getSourceTypeLabel(group.type)}
          </Typography>

          <List sx={{ p: 0 }}>
            {group.rows.map(({ source, index, n }) => {
              // Every row in a numbered menu carries its number. The number is
              // this row's place in its group, which is true of a provenance row
              // as much as a cited one — and numbering only some of them left
              // gaps mid-group that read as a bug. `numbered` still turns the
              // whole column off for a turn that cited nothing, where there is
              // no chip for any number to answer to.
              const rowNumbered = numbered;
              const rowIsCited = index < citedThrough;
              const isAvailable = source.isAvailable ?? Boolean(source.url);
              const clickable = Boolean(isAvailable && source.url);
              const type = sourceGroupType(source);
              const host = hostFromUrl(source.url);
              const title =
                (source.title && source.title.trim()) || host || t('sources.externalSource');
              // What to print under the title and in which form. A quote is
              // set as a quotation, a page summary or an extraction as prose, a
              // field from the record as a metadata line — so a reader can tell
              // the three apart without a label saying which is which. Decided
              // by `sourceSubtext`, shared with mobile and the widget.
              const subtext = sourceSubtext(source);

              return (
                <ListItem key={`source-${index}`} sx={{ p: 0 }} id={`source-${index}`}>
                  {/* A real anchor, so keyboard (Enter), mouse, and
                      modifier/middle clicks all behave natively and keyboard
                      users never navigate the SPA away from the chat. */}
                  <ListItemButton
                    component="a"
                    href={clickable ? source.url : undefined}
                    target={clickable ? '_blank' : undefined}
                    rel={clickable ? 'noopener noreferrer' : undefined}
                    disabled={!clickable}
                    // An unnumbered row is not citation N, so it is
                    // announced by what it is. Naming a number no chip prints
                    // would leave a screen reader user searching the body for
                    // it while a sighted reader sees no number at all.
                    aria-label={
                      rowNumbered
                        ? t('citation.label', {
                            n,
                            title: t('sources.screenReaderLabel', {
                              type: getSourceTypeLabel(type),
                              title,
                            }),
                          })
                        : t('sources.screenReaderLabel', {
                            type: getSourceTypeLabel(type),
                            title,
                          })
                    }
                    onClick={() => {
                      if (clickable) {
                        // Publisher/domain only — no full URL or PII.
                        analytics.track('Chat Source Opened', {
                          publisher: getPublisherName(source),
                          source_type: type,
                          // Position in the whole sources array, which is what
                          // this property has always reported. Grouping changed
                          // what a reader SEES on the row, so the group-scoped
                          // ordinal goes in its own property rather than
                          // redefining this one under existing dashboards.
                          citation_number: index + 1,
                          citation_number_in_group: n,
                          // Whether a chip in the body points at this row. The
                          // menu now also lists what the turn read without
                          // citing, and those rows sit past the cited ones — so
                          // `citation_number` keeps counting positions while
                          // this says which kind of row was opened.
                          is_cited: rowIsCited,
                          host,
                        });
                      }
                    }}
                    sx={{
                      borderRadius: BORDER_RADIUS,
                      py: 0.75,
                      px: 1,
                      // Column so a blurb can sit under the row rather than
                      // competing with the title for horizontal space.
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 0.25,
                      cursor: clickable ? 'pointer' : 'default',
                      '&:hover': {
                        backgroundColor: clickable ? `${primitiveColors.blue1}40` : 'transparent',
                      },
                      '&.Mui-disabled': { opacity: 0.7 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {rowNumbered && (
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            backgroundColor: isAvailable
                              ? `${primitiveColors.rockSlate}12`
                              : `${primitiveColors.rockSlate}08`,
                            color: isAvailable
                              ? primitiveColors.rockSlate
                              : `${primitiveColors.rockSlate}80`,
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          {n}
                        </Box>
                      )}

                      <Box sx={{ flexShrink: 0, display: 'flex' }}>
                        <SourceFavicon url={source.url} type={type} isAvailable={!!isAvailable} />
                      </Box>

                      <Typography
                        variant="body2"
                        component="span"
                        fontWeight={500}
                        color={
                          isAvailable ? primitiveColors.rockSlate : `${primitiveColors.rockSlate}80`
                        }
                        sx={{
                          fontSize: 13,
                          lineHeight: 1.3,
                          flex: '1 1 auto',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {title}
                      </Typography>

                      {host && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            flexShrink: 0,
                            color: `${primitiveColors.rockSlate}80`,
                          }}
                        >
                          {host}
                        </Typography>
                      )}
                    </Box>

                    {subtext.form !== 'none' && (
                      <Typography
                        component="span"
                        sx={{
                          // Hangs under the title, not the badge: badge (22) +
                          // gap (8) + favicon (20) + gap (8) when numbered, and
                          // just the favicon and its gap when not.
                          pl: rowNumbered ? '58px' : '28px',
                          fontSize: subtext.form === 'meta' ? 10 : 11,
                          lineHeight: 1.4,
                          // 0.7 alpha rather than the 0.5 the host label uses:
                          // this is 11px body copy, which WCAG 1.4.3 holds to
                          // 4.5:1, and 0.5 on white computes to 3.04:1. 0.7
                          // reads as the same quiet grey at 5.50:1.
                          color: `${primitiveColors.rockSlate}B3`,
                          // Two lines is enough to say what the source is
                          // without the menu turning into a wall of prose. One
                          // for a metadata line: an index entry that wraps is a
                          // sentence pretending to be one.
                          display: '-webkit-box',
                          WebkitLineClamp: subtext.form === 'meta' ? 1 : 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          ...(subtext.form === 'quote' && {
                            fontStyle: 'italic',
                            // The brand accent as a *rule*, not as text: it
                            // clears the 3:1 WCAG 1.4.11 asks of a graphical
                            // object and not the 4.5:1 body copy this size
                            // needs, so it marks the quote without colouring a
                            // word of it. Same value the widget's `--vm-primary`
                            // and mobile's `brandPrimary` resolve to, so a quote
                            // row reads the same on all three clients.
                            borderLeft: `2px solid ${semanticColors.brandPrimary}`,
                            ml: rowNumbered ? '58px' : '28px',
                            pl: 1,
                          }),
                          ...(subtext.form === 'meta' && {
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            letterSpacing: '0.02em',
                            fontVariantNumeric: 'tabular-nums',
                          }),
                        }}
                      >
                        {subtext.form === 'quote' ? `\u201C${subtext.text}\u201D` : subtext.text}
                        {subtext.date && (
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              mt: '2px',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              fontSize: 10,
                              fontStyle: 'normal',
                              letterSpacing: '0.04em',
                            }}
                          >
                            {subtext.date}
                          </Box>
                        )}
                      </Typography>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Paper>
  );
};

export default SourceList;
